"use client";

import React, { useState } from "react";
import ScrollReveal from "@/components/common/ScrollReveal";
import Tabs, { TAB_ORIENTATION, TabItem } from "@/components/common/Tab";
import DRAMADiagram from "./DRAMADiagram";

interface DramaStep {
  id: string;
  name: string;
  content: React.ReactNode;
}

const manualReviewCategories = [
  "Business Logic",
  "Data Validation",
  "Authentication",
  "Authorization",
  "Session Management",
  "Implementation of Cryptography Service",
  "Error Handling",
  "Logging",
  "Security Misconfiguration",
  "Architecture",
];

const dramaSteps: DramaStep[] = [
  {
    id: "step-1",
    name: "Define",
    content: (
      <div className="flex flex-col gap-6 sm:gap-7 body-1 text-text-highlight">
        <p>
          ECQ works with customer to define the scope of work and gain an overall understanding of
          the target application such as the type of application, business purpose, the programming
          languages, and lines of code (LoC). ECQ Consultants also advise the methodology and
          framework to be used depends on the requirements and scope of work.
        </p>
      </div>
    ),
  },
  {
    id: "step-2",
    name: "Recon",
    content: (
      <div className="flex flex-col gap-6 sm:gap-7 body-1 text-text-highlight">
        <p>
          ECQ starts to Recon the application to gather more information about its architecture such
          as framework, libraries, 3rd party components, and dependencies. ECQ also collects data
          flow diagram and business flow diagram of the applications to further understand how the
          application processes data and how the data move through different components of the
          application. Once the architecture and design of the application are understood, the
          Consultants proceed to identify known issues such as vulnerability and attack vector that
          are related to the framework or libraries used.
        </p>
      </div>
    ),
  },
  {
    id: "step-3",
    name: "Analyze",
    content: (
      <div className="flex flex-col gap-6 sm:gap-7 body-1 text-text-highlight">
        <p>
          ECQ carries out both automated and manual code analysis in this phase to discover
          application attack surface and carry out vulnerability analysis based on the results of
          automated code analysis tools. Through manual application review, the Consultants
          decompose the application to identify all possible application inputs, configuration,
          attack vector, access control matrix and roles, and dependencies.
        </p>
        <p>
          In parallel with manual application analysis, ECQ uses a combination of static and dynamic
          code analysis tools to perform automated scans of the source code and application. The
          initial scan of source code using SAST tool allows ECQ Consultants to quickly understand
          the overview of the application functions and security issues.
        </p>
        <p>
          DAST tool helps identify vulnerabilities and inputs that are most visible from a Black Box
          perspective. Combining the results of DAST with manual code review helps ECQ rank the
          severity and risk of the vulnerable code block with better accuracy.
        </p>
      </div>
    ),
  },
  {
    id: "step-4",
    name: "Manual Review",
    content: (
      <div className="flex flex-col gap-6 sm:gap-7 body-1 text-text-highlight">
        <p>
          This phase focuses primarily in manual code review effort by the Consultants. ECQ will
          thoroughly review the source code to identify vulnerabilities that might not be found or
          discovered by automated tools. ECQ Consultants follow the checklist and guidelines
          provided by OWASP Code Review to ensure important and high-risk vulnerabilities are
          carefully reviewed and tested.
        </p>
        <p>Manual application assessment and code review covers the following categories.</p>
        <ul className="flex flex-col gap-2">
          {manualReviewCategories.map((item) => (
            <li key={item} className="flex items-center gap-3">
              <span
                aria-hidden
                className="inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary"
              />
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p>
          ECQ manually reviews the results of static and dynamic code analysis from the previous
          phase to reduce false positive issues and validates the vulnerability by writing exploit
          or performing a simple proof-of-concept (PoC) attack to demonstrate exploitability of
          critical or high risk vulnerabilities. This activity helps the Consultants to prioritize
          the threat better.
        </p>
      </div>
    ),
  },
  {
    id: "step-5",
    name: "Advise",
    content: (
      <div className="flex flex-col gap-6 sm:gap-7 body-1 text-text-highlight">
        <p>
          This is the final phase of the DRAMA service in which the ECQ works on mitigation measures
          for the identified issues. All the code snippets, evidences, PoC, screenshots, automated
          scan reports, and mitigation measures from the Consultants are submitted to ECQ Technical
          Writer to prepare for the report deliverables.
        </p>
      </div>
    ),
  },
];

const SecureCodeReview = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const total = dramaSteps.length;

  const handleSelect = (idx: number) => {
    if (idx >= 0 && idx < total) setActiveIndex(idx);
  };

  const tabItems: TabItem[] = dramaSteps.map((step, idx) => ({
    id: step.id,
    name: step.name,
    value: step.id,
    number: idx + 1,
    content: step.content,
  }));

  return (
    <section className="relative w-full px-4 sm:px-6 lg:px-8 pb-20 pt-2">
      <div className="relative z-10 mx-auto w-full max-w-[1300px] flex flex-col gap-10 lg:gap-12">
        {/* Intro copy (max ~530px wide per Figma) */}
        <div className="flex flex-col gap-6 sm:gap-7 max-w-[640px] text-text-highlight">
          <ScrollReveal direction="up" delay={0}>
            <p className="body-1">
              Secure Code Review helps identify vulnerabilities in source code of the application
              during the development phase and allows organizations to fix and address those
              identified issues before deploying to the real world. ECQ&apos;s DRAMA code review
              approach offers both static and dynamic code analysis together with exploitation or
              Proof-of-Concept where possible to provide better severity rating for the vulnerable
              code block.
            </p>
          </ScrollReveal>
          <ScrollReveal direction="up" delay={100}>
            <p className="body-1">
              DRAMA code review approach contains five different phases: Define, Recon, Analyze,
              Manual Review, and Advise.
            </p>
          </ScrollReveal>
        </div>

        {/* DRAMA radial diagram */}
        <ScrollReveal direction="up" delay={150}>
          <div className="w-full flex justify-center pt-2">
            <DRAMADiagram activeStep={activeIndex} onStepClick={handleSelect} />
          </div>
        </ScrollReveal>

        {/* Step list + content panel (shared vertical Tabs, synced w/ diagram) */}
        <Tabs
          tabs={tabItems}
          orientation={TAB_ORIENTATION.VERTICAL}
          showContent
          currentValue={dramaSteps[activeIndex].id}
          onChange={(value) => {
            const idx = dramaSteps.findIndex((step) => step.id === value);
            if (idx !== -1) setActiveIndex(idx);
          }}
        />
      </div>
    </section>
  );
};

export default SecureCodeReview;
